import { ISimplePromotionConfiguration } from '@barkinglabs/promotions-types';

/**
 * Resolves the coupon code from a promotion configuration. `localizedText[]`
 * is the source of truth — that's what fi-admin writes today and what the
 * banner reads. The top-level `configuration.couponCode` is a DEPRECATED
 * legacy field (admin no longer surfaces it since barkinglabs/admin#1715,
 * 2026-03-02), preserved only as a safety net for older promotions.
 *
 * All new code that needs a promo's coupon MUST go through this function
 * (or its `promotions-react` equivalent). Reading `configuration.couponCode`
 * directly is wrong: pre-#1715 promos still have the field populated, but
 * post-#1715 promos do not — so the value you read won't match what the
 * customer sees on the banner.
 *
 * Mirrors promotions-react's internal resolveLocalizedText so the code
 * we apply matches the code the banner displays.
 *
 * Resolution order:
 *   1. Exact match on (language, country)
 *   2. `defaultForCountry: true` entry for the same country
 *   3. Any entry for the same country
 *   4. Top-level couponCode (DEPRECATED — only reached by legacy data)
 */
export function resolvePromotionCouponCode(
  config: ISimplePromotionConfiguration,
  language?: string,
  country?: string,
): string | undefined {
  const entries = config.localizedText;
  if (!entries || entries.length === 0) {
    return config.couponCode;
  }

  if (language && country) {
    const exact = entries.find((e) => e.language === language && e.country === country);
    if (exact) return exact.couponCode;
  }

  if (country) {
    const countryDefault = entries.find(
      (e) => e.country.toUpperCase() === country.toUpperCase() && e.defaultForCountry,
    );
    if (countryDefault) return countryDefault.couponCode;

    const anyCountry = entries.find((e) => e.country.toUpperCase() === country.toUpperCase());
    if (anyCountry) return anyCountry.couponCode;
  }

  return config.couponCode;
}
